import { FetchMoreOptions, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { useParams } from "react-router";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { NamedWebhooksIntegration } from "types/generated";

import { GET_NAMED_WEBHOOK } from "./gql";

type GetWebhookData = {
  namedWebhooksIntegration: NamedWebhooksIntegration;
};

const updateWebhookDeliveriesQuery: FetchMoreOptions<GetWebhookData>["updateQuery"] = (
  prev,
  { fetchMoreResult }
) => {
  if (!fetchMoreResult) {
    return prev;
  }

  if (
    prev?.namedWebhooksIntegration.deliveries?.nodes &&
    fetchMoreResult.namedWebhooksIntegration.deliveries?.nodes
  ) {
    return {
      namedWebhooksIntegration: {
        ...fetchMoreResult.namedWebhooksIntegration,
        deliveries: {
          ...fetchMoreResult.namedWebhooksIntegration.deliveries,
          nextToken: fetchMoreResult.namedWebhooksIntegration.deliveries.nextToken,
          nodes: [
            ...(prev.namedWebhooksIntegration.deliveries.nodes || []),
            ...(fetchMoreResult.namedWebhooksIntegration.deliveries.nodes || []),
          ],
        },
      },
    };
  }

  return { namedWebhooksIntegration: { ...prev.namedWebhooksIntegration } };
};

const useWebhookDeliveries = () => {
  const { integrationId: webhookId } = useParams<{ integrationId: string }>();
  const { onError } = useTypedContext(FlashContext);

  const {
    error,
    fetchMore: fetchMoreWebhookDeliveries,
    loading,
    data,
    previousData,
  } = useQuery<GetWebhookData>(GET_NAMED_WEBHOOK, {
    variables: {
      id: webhookId,
      nextToken: null,
    },
    nextFetchPolicy: "cache-first",
  });

  const webhook = data?.namedWebhooksIntegration ?? previousData?.namedWebhooksIntegration;
  const deliveries = webhook?.deliveries?.nodes ?? [];
  const loadingContent = loading && !webhook;
  const hasNextPage = !!webhook?.deliveries?.nextToken;

  const loadMoreItems = useCallback(async () => {
    try {
      const cursor = webhook?.deliveries?.nextToken;
      if (webhook && cursor) {
        await fetchMoreWebhookDeliveries({
          updateQuery: updateWebhookDeliveriesQuery,
          variables: {
            id: webhookId,
            nextToken: cursor,
          },
        });
      }
    } catch (error) {
      onError(error);
    }
  }, [fetchMoreWebhookDeliveries, onError, webhook, webhookId]);

  return {
    webhook,
    deliveries,
    error,
    loadingContent,
    hasNextPage,
    loadMoreItems,
  };
};

export default useWebhookDeliveries;
